/**
 * Guide — parcours de prise en main et gestes de page.
 *
 * Un parcours = une liste de sujets courts ; un sujet se lit en une minute,
 * se joue sur l'écran (« Me montrer ») et se coche quand c'est fait. La
 * progression vit dans le navigateur (localStorage) : c'est un référentiel
 * GLOBAL, identique pour tous les clients, comme les fiches Savoirs.
 *
 * Depuis le 2026-09-16, un sujet appartient à la fois à son PARCOURS et aux
 * PAGES qu'il concerne (`pages`) : le volet Savoirs le liste dans le guide
 * complet ET en tête de « Sur cette page » quand on ouvre l'un de ces écrans,
 * où le mode Guide en pose le repère. Un sujet s'écrit une fois.
 *
 * Un parcours porte son MODULE (`module`, l'id de bande du menu) : le volet ne
 * montre que L'essentiel et le parcours du module où l'on est (2026-09-16),
 * les sujets de la page ouverte en tête. On ne suit une marche à suivre que
 * dans son module, et l'on ne mène personne dans un module qu'il n'a pas.
 *
 * Structure d'un sujet :
 *   { id, title, summary, pages?: [pageKey], steps: [{ text, target?, click?, pageKey?, icon?, openPanel? }] }
 *
 * Sur une étape : `target` = clé du registre des cibles (components/guide/
 * targets.js) ou d'un attribut `data-tour` posé dans l'écran ; `click` = cible
 * à cliquer AVANT de viser (un onglet, par exemple : `tab:moves`) ;
 * `pageKey` = navigation préalable ; `icon` + marqueur {icon} = icône citée
 * dans le texte ; `openPanel` force l'ouverture du panneau du menu. Dès qu'une
 * étape a une cible, le sujet gagne « Me montrer » et un repère sur sa page.
 */

const ONBOARDING = [
  {
    id: 'essentiel',
    label: 'L’essentiel',
    tag: 'essentiel',
    intro:
      'Le socle commun à toutes les pages : le menu, le client et ' +
      'l’exercice, les onglets, les exports.',
    topics: [
      {
        id: 'menu-modules',
        title: 'Le menu : modules et sections',
        summary:
          'Chaque icône de la ligne du haut est un module ; le panneau de ' +
          'gauche liste ses sections.',
        steps: [
          {
            text: 'Chaque icône de cette ligne est un module : Cockpit, Finance, People, Analytique Services, Ventes & Ops. Les vôtres : {modules}.',
            target: 'modules',
          },
          {
            text: 'Cliquer une icône ouvre le panneau du module et sa première section. Ici, People.',
            target: 'band-rh',
          },
          {
            text: 'La punaise {icon} garde le panneau des sections ouvert. Libéré, il s’efface et revient au survol du bord gauche.',
            icon: 'pin',
            target: 'panel-pin',
            openPanel: true,
          },
        ],
      },
      {
        id: 'client-exercice',
        title: 'Navigation générale',
        summary:
          'La barre supérieure porte le client et l’exercice ; le pied du ' +
          'menu porte la sortie.',
        steps: [
          {
            text: 'Le client actif s’affiche ici ; cliquer son nom permet d’en changer quand on suit plusieurs dossiers.',
            target: 'client',
          },
          {
            text: 'À droite, le dernier mois réel clôturé et la date du jour, puis l’exercice : les flèches ‹ › changent l’année et toute la page se recalcule.',
            target: 'year',
          },
          {
            text: 'La roue dentée ouvre les Paramètres du client : devise, première année d’analyse, langue, TVA.',
            target: 'settings',
          },
          {
            text: 'Le bouton en bas du menu déconnecte de Palestre.',
            target: 'logout',
          },
        ],
      },
      {
        id: 'hubs-onglets',
        title: 'Les hubs et leurs onglets',
        summary:
          'Une section ouvre souvent un hub : ses vues sont des onglets.',
        /* Sans scène garantie : l'Accueil n'a plus d'onglets, et les hubs qui
           en ont dépendent des modules vendus. Le sujet se joue donc sur la
           page ouverte quand elle en porte ; à défaut, la bulle se lit au
           centre, sans halo. */
        steps: [
          {
            text: 'Les états ou les vues d’une section sont ses onglets, sur la ligne en tête de la page.',
            target: 'tabs',
          },
          {
            text: 'Cette ligne porte aussi ce qui agit sur l’écran : unité, vue, filtres, et parfois l’année.',
            target: 'tabs',
          },
        ],
      },
      {
        id: 'exports',
        title: 'Télécharger : Excel et PDF',
        summary:
          'Un seul point de sortie : l’icône télécharger de la barre supérieure.',
        steps: [
          {
            text: 'L’icône télécharger est le seul point de sortie de l’application. Excel sort tout le hub d’un coup, un onglet par feuille ; atténuée, elle dit que la page n’a rien à exporter.',
            target: 'export',
          },
        ],
      },
      {
        id: 'savoirs-lexique',
        title: 'Savoirs et Guide',
        summary:
          'L’ampoule ouvre Savoirs ; le chemin montre les gestes sur l’écran.',
        steps: [
          {
            text: 'L’ampoule ouvre le panneau Savoirs : le Guide (les marches à suivre), les Fiches (les notions) et le Lexique. Un point dit que la page ouverte a quelque chose à proposer.',
            target: 'band-knowledge',
          },
          {
            text: 'Le chemin, à côté de la roue dentée, allume les repères ambre du Guide sur la page. Atténué, la page n’a rien à montrer.',
            target: 'guide-button',
          },
        ],
      },
    ],
  },
  {
    id: 'people',
    label: 'People',
    tag: 'people',
    module: 'rh',
    intro:
      'L’effectif : qui est là, ce qui bouge, ce que cela coûte.',
    topics: [
      {
        id: 'people-mouvement',
        title: 'Simuler les mouvements internes',
        summary:
          'Un mouvement simule une arrivée, un départ ou un changement de ' +
          'taux sur les mois ouverts. Il ne réécrit jamais un mois clos.',
        pages: ['hr-headcount'],
        steps: [
          {
            text: 'Depuis la Synthèse des effectifs, la tuile « Poser un mouvement » ouvre la fenêtre de saisie sur l’onglet Mouvements : nature, personne ou poste, date et taux.',
            pageKey: 'hr-headcount',
            click: 'tab:summary',
            target: 'headcount-door-move',
          },
          {
            text: 'L’onglet Mouvements range ce qui est posé par nature. Le ＋ d’une colonne ajoute un mouvement de cette nature ; le mois ouvert porte la pastille « à confirmer ».',
            click: 'tab:moves',
            target: 'headcount-moves-columns',
          },
          {
            text: 'De retour sur la Synthèse, la frise relit aussitôt le mouvement dans sa vue Simulation. Les mois clos ne bougent pas : un mouvement n’écrit jamais le passé.',
            click: 'tab:summary',
            target: 'headcount-chart',
          },
        ],
      },
      {
        id: 'people-collaborateur',
        title: 'Ajouter un collaborateur',
        summary:
          'Une personne se crée une fois, dans Salaires › Collaborateurs, ' +
          'avec son premier contrat ; l’effectif et la masse salariale la ' +
          'lisent aussitôt.',
        pages: ['hr-headcount', 'hr-personnel'],
        steps: [
          {
            text: 'La tuile « Mettre à jour les données » ouvre le panneau des données People : ajouter une personne, en importer plusieurs, charger les compléments.',
            pageKey: 'hr-headcount',
            click: 'tab:summary',
            target: 'headcount-door-data',
          },
          {
            text: 'Une fois le contrat enregistré, la masse salariale chargée de l’exercice et de l’exercice suivant se recalcule ici.',
            target: 'headcount-kpis',
          },
        ],
      },
      {
        id: 'people-tuiles',
        title: 'Ce que disent les quatre tuiles',
        summary:
          'Le trajet en EPT du 1er janvier à décembre, la moyenne de ' +
          'l’exercice, puis la masse salariale chargée de l’exercice et du ' +
          'suivant.',
        pages: ['hr-headcount'],
        steps: [
          {
            text: 'Une tuile par question : d’où on part et où on arrive (EPT au 01.01 → décembre), ce que l’année a porté en moyenne, et ce que l’effectif coûte, cette année et la prochaine, charges comprises.',
            pageKey: 'hr-headcount',
            click: 'tab:summary',
            target: 'headcount-kpis',
          },
        ],
      },
      {
        id: 'people-frise',
        title: 'Lire la frise à trois temps',
        summary:
          'Année antérieure, réalisé, prévisionnel : trois teintes pour une ' +
          'même frise. La ligne traitillée marque l’EPT au 1er janvier de ' +
          'l’exercice et ne suit pas la fenêtre choisie.',
        pages: ['hr-headcount'],
        steps: [
          {
            text: 'La frise mensuelle lit l’effectif en EPT. Famille navy pour l’année antérieure, ardoise claire pour le réalisé, ardoise pleine pour le prévisionnel.',
            pageKey: 'hr-headcount',
            click: 'tab:summary',
            target: 'headcount-chart',
          },
          {
            text: 'La vue Contrats lit ce qui est signé, la vue Simulation y ajoute les mouvements. Les deux coïncident sur tout mois clos. La période choisit la fenêtre, pas le point de départ.',
            target: 'headcount-views',
          },
          {
            text: 'L’onglet Frise détaille la même projection personne par personne.',
            click: 'tab:actual',
            target: 'tabs',
          },
        ],
      },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    tag: 'finance',
    module: 'financials',
    intro:
      'Le réel de l’entreprise : on le charge, on affecte les comptes, les ' +
      'rapports le lisent.',
    topics: [
      {
        id: 'finance-import-excel',
        title: 'Mettre à jour le réel en chargeant un Excel',
        summary:
          'Le compte de résultat de la comptabilité se charge d’un fichier : ' +
          'une ligne par compte, un montant par mois. Les comptes nouveaux ' +
          'arrivent « non mappés », on les affecte ensuite à leur ligne.',
        pages: ['financials-data-hub'],
        steps: [
          {
            text: 'Données › Compte de résultat : « Importer Excel » prend le fichier de ta comptabilité. Une ligne par compte : le numéro en première colonne, le libellé en deuxième, puis un montant par mois sous des entêtes 1 à 12 ; produits en positif, charges en négatif. Le bilan se charge de la même façon sous l’onglet Bilan.',
            pageKey: 'financials-data-hub',
            click: 'tab:pl',
            target: 'pl-import',
          },
          {
            text: 'L’année n’est pas dans le fichier : elle t’est demandée à l’import, l’exercice affiché en proposition. Le réel se relit ici, mois par mois, jusqu’au dernier mois clôturé ; les mois chargés remplacent ceux du même exercice.',
            target: 'pl-actuals',
          },
          {
            text: 'Affectation des comptes : le compteur dit combien de comptes restent à mapper. « Éditer » affecte un compte à sa ligne de reporting ; le pinceau fait l’inverse, on choisit une ligne puis on clique les comptes.',
            click: 'tab:pl-mapping',
            target: 'pl-mapping-count',
          },
          {
            text: 'Les Rapports lisent aussitôt ce réel : Synthèse, compte de résultat, bilan, flux. Un rapport ne s’édite jamais, mettre à jour se fait ici, à une seule adresse.',
            target: 'band-financials',
          },
        ],
      },
    ],
  },
  {
    id: 'sales',
    label: 'Ventes & Opérations',
    tag: 'sales',
    module: 'scenarii',
    intro:
      'Le catalogue de ce que l’entreprise vend, puis sa trajectoire par année.',
    topics: [
      {
        id: 'sales-offre',
        title: 'Créer une offre',
        summary:
          'Une offre est une équation de chiffre d’affaires : volume × prix, ' +
          'temps × tarif, base × revenu unitaire… On choisit comment on vend, ' +
          'la fenêtre pose le reste.',
        pages: ['sales-offers-list'],
        steps: [
          {
            text: '« ＋ Offre » ouvre la fenêtre de création : le modèle choisi fixe l’équation, la facturation et la reconnaissance du revenu ; il reste le nom, le compte de CA et le prix indicatif.',
            pageKey: 'sales-offers-list',
            target: 'offers-add',
          },
        ],
      },
      {
        id: 'sales-trajectoire',
        title: 'Planifier la trajectoire',
        summary:
          'Par offre et par année : un volume, un prix, un coût unitaire. Le ' +
          'chiffre d’affaires se calcule au serveur ; un prix vide reprend ' +
          'l’année précédente.',
        pages: ['sales-trajectory'],
        steps: [
          {
            text: 'Chaque ligne est une offre, chaque colonne une année de l’horizon de planification. On tape le volume et le prix ; le CA en découle.',
            pageKey: 'sales-trajectory',
            target: 'trajectory-table',
          },
          {
            text: '« ＋ ligne » découpe une offre par client, segment ou tarif : l’offre devient la somme de ses lignes et son prix la moyenne pondérée, en lecture seule.',
            target: 'trajectory-addline',
          },
        ],
      },
    ],
  },
];

/** Liste plate ordonnée des sujets, chacun avec son parcours. */
export const TOPICS = ONBOARDING.flatMap(g => g.topics.map(t => ({ ...t, guide: g })));

/** Sujets qui concernent une page (dans l'ordre des parcours). */
export function topicsForPage(pageKey) {
  if (!pageKey) return [];
  return TOPICS.filter(t => (t.pages || []).includes(pageKey));
}

export default ONBOARDING;
